// Figurinha animada das Mensagens (0.51): emoji ou foto, frase e animação, desenhadas pelo próprio app.
// Funciona igual no Android, no iPhone e no navegador, sem baixar nada.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, Image, Platform, Text, View } from 'react-native';
import type { Fig } from '../store/types';
import { urlDoAnexo } from '../lib/chat';

const NATIVO = Platform.OS !== 'web';

/** Laço de animação (0 → 1 → 0...). */
function useLaco(dur: number, ligado: boolean, vaiEVolta = true) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!ligado) { v.setValue(0); return; }
    const a = Animated.loop(vaiEVolta
      ? Animated.sequence([Animated.timing(v, { toValue: 1, duration: dur / 2, easing: Easing.inOut(Easing.quad), useNativeDriver: NATIVO }),
        Animated.timing(v, { toValue: 0, duration: dur / 2, easing: Easing.inOut(Easing.quad), useNativeDriver: NATIVO })])
      : Animated.timing(v, { toValue: 1, duration: dur, easing: Easing.linear, useNativeDriver: NATIVO }));
    a.start();
    return () => a.stop();
  }, [ligado, dur]); // eslint-disable-line react-hooks/exhaustive-deps
  return v;
}

/** Partículas que sobem (corações, brilhos) ou caem (confete). */
function Particulas({ tipo, tam, ligado }: { tipo: 'coracoes' | 'confete' | 'brilho'; tam: number; ligado: boolean }) {
  const n = 6;
  const itens = useMemo(() => Array.from({ length: n }, (_, i) => ({
    x: ((i * 37) % 100) / 100, atraso: i * 260, e: tipo === 'coracoes' ? ['❤️', '💖', '💕'][i % 3] : tipo === 'confete' ? ['🎊', '✨', '🎉', '⭐'][i % 4] : ['✨', '⭐', '💫'][i % 3],
  })), [tipo]);
  return (
    <View pointerEvents="none" style={{ position: 'absolute', left: 0, top: 0, width: tam, height: tam }}>
      {itens.map((p, i) => <Particula key={i} {...p} tipo={tipo} tam={tam} ligado={ligado} />)}
    </View>
  );
}
function Particula({ x, atraso, e, tipo, tam, ligado }: { x: number; atraso: number; e: string; tipo: string; tam: number; ligado: boolean }) {
  const v = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!ligado) return;
    const a = Animated.loop(Animated.sequence([Animated.delay(atraso), Animated.timing(v, { toValue: 1, duration: 1600, easing: Easing.out(Easing.quad), useNativeDriver: NATIVO }), Animated.timing(v, { toValue: 0, duration: 0, useNativeDriver: NATIVO })]));
    a.start();
    return () => a.stop();
  }, [ligado]); // eslint-disable-line react-hooks/exhaustive-deps
  const cai = tipo === 'confete';
  const ty = v.interpolate({ inputRange: [0, 1], outputRange: cai ? [-tam * 0.1, tam * 0.85] : [tam * 0.75, tam * 0.05] });
  const op = v.interpolate({ inputRange: [0, 0.15, 0.8, 1], outputRange: [0, 1, 1, 0] });
  const sc = v.interpolate({ inputRange: [0, 1], outputRange: tipo === 'brilho' ? [0.4, 1.2] : [0.7, 1] });
  return (
    <Animated.Text style={{ position: 'absolute', left: x * tam * 0.85, fontSize: tam * 0.13, opacity: ligado ? op : 0, transform: [{ translateY: ty }, { scale: sc }] }}>{e}</Animated.Text>
  );
}

export function FigurinhaView({ f, tam = 150, animar = true }: { f: Omit<Fig, 'id'>; tam?: number; animar?: boolean }) {
  const a = f.a ?? 'nada';
  const ligado = animar && a !== 'nada';
  const v = useLaco(a === 'pulsa' ? 900 : a === 'treme' ? 260 : a === 'flutua' ? 2400 : a === 'gira' ? 1200 : 1000, ligado);
  const [url, setUrl] = useState<string | null>(f.url ?? null);
  useEffect(() => { if (f.img) urlDoAnexo(f.img).then(setUrl).catch(() => {}); else setUrl(f.url ?? null); }, [f.img, f.url]);

  const tf: any[] = [];
  if (ligado) {
    if (a === 'pulo') tf.push({ translateY: v.interpolate({ inputRange: [0, 1], outputRange: [0, -tam * 0.12] }) });
    if (a === 'pulsa' || a === 'coracoes') tf.push({ scale: v.interpolate({ inputRange: [0, 1], outputRange: [1, 1.12] }) });
    if (a === 'gira') tf.push({ rotate: v.interpolate({ inputRange: [0, 1], outputRange: ['-12deg', '12deg'] }) });
    if (a === 'treme') tf.push({ translateX: v.interpolate({ inputRange: [0, 1], outputRange: [-tam * 0.03, tam * 0.03] }) });
    if (a === 'flutua') tf.push({ translateY: v.interpolate({ inputRange: [0, 1], outputRange: [tam * 0.04, -tam * 0.04] }) });
    if (a === 'brilho' || a === 'confete') tf.push({ scale: v.interpolate({ inputRange: [0, 1], outputRange: [0.96, 1.05] }) });
  }
  const temFrase = !!f.t?.trim();
  const centro = temFrase ? tam * 0.62 : tam * 0.82;
  return (
    <View style={{ width: tam, height: tam, alignItems: 'center', justifyContent: 'center' }}>
      {!!f.c && !f.url && <View style={{ position: 'absolute', width: tam * 0.88, height: tam * 0.88, borderRadius: tam * 0.44, backgroundColor: f.c, opacity: 0.9 }} />}
      <Animated.View style={{ alignItems: 'center', justifyContent: 'center', transform: tf }}>
        {f.img || f.url ? (
          url ? <Image source={{ uri: url }} style={{ width: f.url ? tam : centro, height: f.url ? tam * Math.min(1.2, (f.h ?? 1) / (f.w ?? 1)) : centro, borderRadius: f.url ? 8 : centro / 2,
            borderWidth: f.url ? 0 : Math.max(2, tam * 0.03), borderColor: '#fff' }} resizeMode={f.url ? 'contain' : 'cover'} />
            : <View style={{ width: centro, height: centro, borderRadius: centro / 2, backgroundColor: 'rgba(255,255,255,0.15)' }} />
        ) : <Text style={{ fontSize: centro * 0.78, lineHeight: centro * 0.95 }}>{f.e}</Text>}
      </Animated.View>
      {temFrase && (
        <Text numberOfLines={2} style={{ position: 'absolute', bottom: tam * 0.02, left: 2, right: 2, textAlign: 'center', color: '#fff', fontWeight: '900', fontSize: tam * 0.13,
          textShadowColor: '#000', textShadowRadius: tam * 0.03, textShadowOffset: { width: 0, height: Math.max(1, tam * 0.01) } }}>{f.t}</Text>
      )}
      {ligado && (a === 'coracoes' || a === 'confete' || a === 'brilho') && <Particulas tipo={a} tam={tam} ligado={ligado} />}
    </View>
  );
}
